import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search, ArrowLeft, ArrowRight, Calendar, Clock, Filter } from "lucide-react";
import { Input } from "../components/ui/input";
import { getAllArticles } from "../data/articles";

const CATEGORIES = [
  "All",
  "AI & Automation",
  "Robotics",
  "Semiconductors",
  "Sustainability",
  "Energy Storage",
  "AI & Infrastructure",
  "Predictive Maintenance",
  "Space",
  "Featured Article",
];

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [query, setQuery] = useState(initialQuery);
  const [activeCategory, setActiveCategory] = useState("All");
  const inputRef = useRef(null);
  const articles = getAllArticles();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const q = searchParams.get("q") || "";
    setQuery(q);
  }, [searchParams]);

  const filtered = articles.filter((article) => {
    const q = query.toLowerCase().trim();
    const matchesQuery =
      !q ||
      article.title.toLowerCase().includes(q) ||
      article.category.toLowerCase().includes(q) ||
      (article.summary && article.summary.toLowerCase().includes(q));
    const matchesCategory =
      activeCategory === "All" || article.category === activeCategory;
    return matchesQuery && matchesCategory;
  });

  const handleSearch = (value) => {
    setQuery(value);
    if (value.trim()) {
      setSearchParams({ q: value });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Helmet>
        <title>{query ? `Search: ${query}` : "Search Articles"} | FutureThought</title>
        <meta
          name="description"
          content="Search FutureThought articles on AI, technology, sustainability, and emerging innovations."
        />
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link
              to="/"
              className="text-2xl font-bold font-['Playfair_Display']"
              data-testid="search-page-logo"
            >
              Future<span className="text-[hsl(var(--primary))]">Thought</span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
              data-testid="search-back-home"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Search Header */}
      <div className="pt-28 pb-12 px-6 md:px-12 lg:px-24 border-b border-[hsl(var(--border))]">
        <div className="max-w-3xl mx-auto">
          <p className="text-sm font-mono tracking-wider uppercase text-[hsl(var(--primary))] mb-4">
            Search
          </p>
          <div className="relative">
            <Search
              size={22}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]"
            />
            <Input
              ref={inputRef}
              type="text"
              placeholder="Search articles by title, category, or topic..."
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              data-testid="search-page-input"
              className="h-14 pl-12 pr-4 text-lg bg-[hsl(var(--secondary))] border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] focus:ring-1 focus:ring-[hsl(var(--primary))]"
            />
          </div>

          {/* Category Filters */}
          <div className="mt-6 flex flex-wrap gap-2" data-testid="search-category-filters">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`filter-${cat.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                className={`px-4 py-1.5 text-sm font-medium transition-colors border ${
                  activeCategory === cat
                    ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                    : "bg-transparent text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--primary)/0.5)] hover:text-[hsl(var(--foreground))]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <p
            className="text-sm text-[hsl(var(--muted-foreground))] mb-8"
            data-testid="search-results-count"
          >
            {query
              ? `${filtered.length} result${filtered.length !== 1 ? "s" : ""} for "${query}"`
              : `${filtered.length} article${filtered.length !== 1 ? "s" : ""}`}
            {activeCategory !== "All" && ` in ${activeCategory}`}
          </p>

          {filtered.length === 0 ? (
            <div
              className="text-center py-16"
              data-testid="search-no-results"
            >
              <Search
                size={48}
                className="mx-auto mb-4 text-[hsl(var(--muted-foreground)/0.3)]"
              />
              <h2 className="text-xl font-['Playfair_Display'] font-semibold mb-2">
                No articles found
              </h2>
              <p className="text-[hsl(var(--muted-foreground))]">
                Try a different search term or browse by category.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {filtered.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group block"
                  data-testid={`search-result-card-${article.slug}`}
                >
                  <article className="flex gap-5 p-5 border border-[hsl(var(--border))] hover:border-[hsl(var(--primary)/0.5)] bg-[hsl(var(--card))] transition-all duration-300">
                    <div className="hidden sm:block w-40 h-28 flex-shrink-0 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-mono tracking-wider uppercase text-[hsl(var(--primary))]">
                          {article.category}
                        </span>
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">
                          {article.date}
                        </span>
                      </div>
                      <h3 className="text-lg font-['Playfair_Display'] font-semibold mb-2 group-hover:text-[hsl(var(--primary))] transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] line-clamp-2 leading-relaxed">
                        {article.summary}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--primary))] group-hover:gap-2.5 transition-all">
                        Read Article <ArrowRight size={14} />
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 lg:px-24 border-t border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link
            to="/"
            className="text-xl font-bold font-['Playfair_Display']"
          >
            Future<span className="text-[hsl(var(--primary))]">Thought</span>
          </Link>
          <div className="flex gap-6 text-sm text-[hsl(var(--muted-foreground))]">
            <Link
              to="/privacy-policy"
              className="hover:text-[hsl(var(--foreground))] transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="hover:text-[hsl(var(--foreground))] transition-colors"
            >
              Terms of Service
            </Link>
            <Link
              to="/contact"
              className="hover:text-[hsl(var(--foreground))] transition-colors"
            >
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SearchPage;
